import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from '@refinedev/core';
import { env } from '@/config/env';

/**
 * Sets the browser tab title on a show page to
 * `"<resource>: <record> | <app>"`, e.g. `"Publikacja: Wiedźmin | Novelist Admin"`.
 *
 * The app-wide `DocumentTitleHandler` only knows the generic resource title
 * ("Publikacja") and re-applies it on every location change — including tab
 * switches, which only touch the query string — so this re-asserts ours after
 * it. `document.title` is assigned directly rather than through refine's
 * `useDocumentTitle`, which pipes the string through i18next and would mangle
 * record names containing `:` or `.` ("Wiedźmin: Ostatnie życzenie").
 *
 * Falls back to the handler's generic title until the record loads.
 *
 * @param resource Refine resource name, e.g. `user_catalog.publications`;
 *   its `<resource>.titles.show` translation is used as the prefix.
 * @param recordName The record's display name.
 */
export const useRecordDocumentTitle = (
  resource: string,
  recordName?: string | null
) => {
  const t = useTranslate();
  const location = useLocation();

  useEffect(() => {
    if (!recordName) return;
    document.title = `${t(`${resource}.titles.show`)}: ${recordName} | ${
      env.APP_NAME
    }`;
  }, [resource, recordName, location, t]);
};
